import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Input, Modal, Table, Tag, Typography } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

import { STEP_UP_Z_INDEX, stepUpNote, stepUpWarning } from './notes.js';
import { summaryFiles, summaryRows, summaryTransactions } from './summaryRows.js';

const LABELS = Object.freeze({
    INTAKE_UPLOAD: 'Upload a file',
    INTAKE_UPLOAD_BATCH: 'Upload files',
    INTAKE_ACCEPT: 'Accept a file',
    INTAKE_REJECT: 'Reject a file',
    FUND_FEES_PROPOSE: 'Propose fund fees',
    FUND_FEES_APPROVE: 'Approve fund fees',
    FUND_FEES_REJECT: 'Reject proposed fund fees',
    DAY_END_RUN: 'Run day end'
});

/** An upload batch's files (summaryRows.js summaryFiles): what the one code uploads. */
const FILE_COLUMNS = [
    // The dialog keeps its own width (it opens before the summary arrives): a long file name wraps.
    { title: 'File Name', dataIndex: 'fileName', key: 'fileName', render: (v) => <Typography.Text strong style={{ wordBreak: 'break-all' }}>{v}</Typography.Text> },
    {
        title: 'File Category',
        dataIndex: 'category',
        key: 'category',
        render: (v, f) => (f.account ? <>{v}<div><Typography.Text type="secondary">{f.account}</Typography.Text></div></> : v)
    },
    { title: 'Effective Date', dataIndex: 'effectiveDate', key: 'effectiveDate', width: 115 }
];

/** Run day end's transactions waiting for approval (summaryRows.js summaryTransactions): what the run does with each. */
const TRANSACTION_COLUMNS = [
    { title: 'Transaction', dataIndex: 'transactionRef', key: 'transactionRef', render: (v) => <Typography.Text strong style={{ wordBreak: 'break-all' }}>{v}</Typography.Text> },
    { title: 'Fund', dataIndex: 'fundId', key: 'fundId' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'What Happens', dataIndex: 'outcome', key: 'outcome', render: (v, t) => (t.moved ? <Tag color="gold">{v}</Tag> : <Tag color="blue">{v}</Tag>) }
];

function useSecondsUntil(iso) {
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!iso) return undefined;
        const tick = () => setLeft(Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 1000)));
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [iso]);
    return left;
}

/**
 * The authenticator code for one action (stepup/controller.js): what the
 * SERVER says the action will do, what that sets off (notes.js), its class,
 * the time left on the challenge, and six digits. Closing it cancels the
 * action and nothing else.
 *
 * zIndex STEP_UP_Z_INDEX: an upload is begun from the File Uploads upload
 * dialog (a top-level Modal, 1000), an accept or reject from its drawer, and a
 * reject from the reason dialog inside that, so this dialog must sit above all
 * three. antd stacks a container inside a container (its useZIndex):
 * a top-level Drawer gives its children 1000 + 100 = 1100, and the Modal
 * nested in it is 1100 + 100 = 1200 - so 1100 would put this dialog UNDER the
 * reason dialog. 1300 clears that, and stays below antd's messages (2000+).
 * Settings > Fund config begins a fee proposal and a rejection from a
 * top-level Modal (1000) too, which 1300 also clears.
 */
const StepUpDialog = ({ state, onCode, onRenew, onCancel }) => {
    const left = useSecondsUntil(state.expiresAt);
    const [codeKey, setCodeKey] = useState(0);

    // A fresh challenge (after a wrong code) clears the boxes.
    useEffect(() => {
        setCodeKey((k) => k + 1);
    }, [state.expiresAt]);

    const expired = Boolean(state.expiresAt) && left === 0;
    const note = state.summary ? stepUpNote(state.actionType, state.summary) : null;
    const warning = state.summary ? stepUpWarning(state.actionType, state.summary) : null;
    const files = summaryFiles(state.summary);
    const transactions = summaryTransactions(state.summary);
    const more = Number(state.summary?.moreTransactions ?? 0);

    return (
        <Modal
            open={state.open}
            title={<><SafetyOutlined /> Confirm with your authenticator</>}
            onCancel={onCancel}
            maskClosable={false}
            destroyOnHidden
            zIndex={STEP_UP_Z_INDEX}
            rootClassName="lfoc-styles-wrapper"
            footer={[<Button key="cancel" onClick={onCancel}>Cancel</Button>]}
        >
            <Typography.Paragraph>
                <b>{LABELS[state.actionType] ?? state.actionType}</b>{' '}
                {state.actionClass && <Tag color="gold">{state.actionClass}</Tag>}
            </Typography.Paragraph>
            {state.summary && (
                <Descriptions size="small" column={1} bordered items={summaryRows(state.summary)} className="stepup-summary" />
            )}
            {files && (
                <Table size="small" rowKey="key" columns={FILE_COLUMNS} dataSource={files} pagination={false}
                    className="stepup-files" style={{ marginTop: 12, marginBottom: 12 }} />
            )}
            {warning && <Alert type="warning" showIcon message={warning} className="stepup-warning" style={{ marginTop: 12 }} />}
            {transactions?.length > 0 && (
                <>
                    <Table size="small" rowKey="key" columns={TRANSACTION_COLUMNS} dataSource={transactions} pagination={false}
                        className="stepup-transactions" style={{ marginTop: 12, marginBottom: more ? 4 : 12 }} />
                    {more > 0 && (
                        <Typography.Paragraph type="secondary" className="stepup-more">
                            and {more} more - every one is in Transactions &gt; Approval.
                        </Typography.Paragraph>
                    )}
                </>
            )}
            {note && <Typography.Paragraph type="secondary" className="stepup-note">{note}</Typography.Paragraph>}
            {state.error && <Alert type={state.locked ? 'warning' : 'error'} showIcon message={state.error} className="auth-alert" />}
            {!state.locked && (
                <div className="auth-otp">
                    <Typography.Paragraph>Enter the 6-digit code from your authenticator app.</Typography.Paragraph>
                    <Input.OTP key={codeKey} length={6} size="large" disabled={state.busy || expired} onChange={onCode} autoFocus />
                    <Typography.Paragraph type="secondary" className="stepup-countdown">
                        {expired
                            ? <>This request timed out. <Button type="link" onClick={onRenew} disabled={state.busy}>Try again</Button></>
                            : <>Time left: {left} s</>}
                    </Typography.Paragraph>
                </div>
            )}
        </Modal>
    );
};

export default StepUpDialog;
